import { useCallback, useEffect, useRef, useState } from 'react';

const DEBOUNCE_MS = 1500;
const SAVED_FLASH_MS = 1800;

export function decidePlanAutosaveEffect({ loadingInitial, initialLoadHandled, planText,
  lastSavedText }) {
  if (loadingInitial) return { action: 'reset', initialLoadHandled: false };
  if (!initialLoadHandled) return { action: 'seed', initialLoadHandled: true };
  if (planText === lastSavedText) return { action: 'none', initialLoadHandled: true };
  return { action: 'save', initialLoadHandled: true };
}

/**
 * Contrôleur single-flight latest-wins. Une sauvegarde déjà partie termine son
 * CAS serveur, mais elle n'est jamais publiée dans l'UI si un texte plus récent
 * est en attente. La dernière photographie remplace toute file intermédiaire.
 */
export function createPlanAutosaveQueue({ save, onCanonical = () => {},
  onCommit = () => {}, onError = () => {}, onState = () => {} }) {
  let callbacks = { save, onCanonical, onCommit, onError, onState };
  let inFlight = false;
  let queued = null;
  let latestVersion = 0;
  let disposed = false;

  const drain = async () => {
    if (disposed || inFlight || !queued) return;
    const job = queued;
    queued = null;
    inFlight = true;
    callbacks.onState('saving', job.text);
    try {
      const result = await callbacks.save(job.text, {
        version: job.version,
        meta: job.meta,
        isLatest: () => !disposed && job.version === latestVersion && queued === null,
      });
      if (disposed) {
        job.resolve(Object.freeze({ ok: false, superseded: true, result: null }));
        return;
      }
      await callbacks.onCanonical(result, job.text, job.version);
      let committed = false;
      if (job.version === latestVersion && queued === null) {
        await callbacks.onCommit(result, job.text, job.version);
        if (job.version === latestVersion && queued === null) {
          committed = true;
          callbacks.onState('saved', job.text);
        }
      }
      job.resolve(Object.freeze({ ok: true, committed, superseded: !committed, result }));
    } catch (error) {
      const latest = !disposed && job.version === latestVersion && queued === null;
      if (latest) {
        callbacks.onError(error, job.text, job.version);
        callbacks.onState('error', job.text);
      }
      job.resolve(Object.freeze({ ok: false, superseded: !latest, error, result: null }));
    } finally {
      inFlight = false;
      if (!disposed && queued) void drain();
    }
  };

  return Object.freeze({
    request(text, meta = null) {
      latestVersion += 1;
      if (queued) {
        queued.resolve(Object.freeze({ ok: false, superseded: true, result: null }));
      }
      let resolve;
      const completion = new Promise((done) => { resolve = done; });
      queued = Object.freeze({ text, meta, version: latestVersion, resolve });
      void drain();
      return completion;
    },
    supersede(text) {
      latestVersion += 1;
      if (queued) {
        queued.resolve(Object.freeze({ ok: false, superseded: true, result: null }));
      }
      queued = null;
      callbacks.onState('saved', text);
      return latestVersion;
    },
    updateCallbacks(next) { callbacks = { ...callbacks, ...next }; },
    dispose() {
      disposed = true;
      if (queued) queued.resolve(Object.freeze({ ok: false, superseded: true, result: null }));
      queued = null;
    },
    snapshot() { return Object.freeze({ inFlight, queued: queued?.text ?? null, latestVersion }); },
  });
}

export function usePlanAutosave({ planText, loadingInitial, onSave, onCanonical, onCommit,
  onError }) {
  const [autosaveState, setAutosaveState] = useState('idle');
  const autosaveTimerRef = useRef(null);
  const stateTimerRef = useRef(null);
  const lastSavedTextRef = useRef('');
  const initialLoadHandledRef = useRef(false);
  const [queue] = useState(() => createPlanAutosaveQueue({
      save: onSave,
      onCanonical: onCanonical || (() => {}),
      onCommit: onCommit || (() => {}),
      onError: onError || (() => {}),
      onState: (state) => {
        setAutosaveState(state);
        if (state === 'saved') {
          setTimeout(() => {
            setAutosaveState((current) => (current === 'saved' ? 'idle' : current));
          }, SAVED_FLASH_MS);
        }
      },
    }));

  useEffect(() => {
    queue.updateCallbacks({
      save: onSave,
      onCanonical: onCanonical || (() => {}),
      onCommit: async (result, text, version) => {
        lastSavedTextRef.current = text;
        await onCommit?.(result, text, version);
      },
      onError: onError || (() => {}),
    });
  }, [queue, onSave, onCanonical, onCommit, onError]);

  useEffect(() => () => {
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    if (stateTimerRef.current) clearTimeout(stateTimerRef.current);
    queue.dispose();
  }, [queue]);

  useEffect(() => {
    const decision = decidePlanAutosaveEffect({ loadingInitial,
      initialLoadHandled: initialLoadHandledRef.current, planText,
      lastSavedText: lastSavedTextRef.current });
    initialLoadHandledRef.current = decision.initialLoadHandled;
    if (decision.action === 'reset') return;
    if (decision.action === 'seed') {
      lastSavedTextRef.current = planText;
      stateTimerRef.current = setTimeout(() => setAutosaveState('idle'), 0);
      return;
    }
    if (decision.action === 'none') return;
    if (stateTimerRef.current) clearTimeout(stateTimerRef.current);
    stateTimerRef.current = setTimeout(() => setAutosaveState('dirty'), 0);
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => queue.request(planText), DEBOUNCE_MS);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
  }, [planText, loadingInitial, queue]);

  const markInitial = useCallback((text) => { lastSavedTextRef.current = text; }, []);
  const markSaved = useCallback((text) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    lastSavedTextRef.current = text;
    queue.supersede(text);
  }, [queue]);
  const markError = useCallback(() => setAutosaveState('error'), []);
  const saveNow = useCallback(async (text, meta = null) => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
      autosaveTimerRef.current = null;
    }
    const completion = await queue.request(text, meta);
    if (!completion.ok) {
      if (completion.error) throw completion.error;
      throw new Error('Cette sauvegarde a été remplacée par une version plus récente.');
    }
    if (!completion.committed) {
      throw new Error('Une version plus récente doit terminer sa sauvegarde.');
    }
    return completion.result;
  }, [queue]);

  return { autosaveState, markInitial, markSaved, markError, saveNow };
}
